import React, { FunctionComponent, ReactElement, useEffect } from 'react';
import styled from 'styled-components';
import { defineMessages, useIntl } from 'react-intl';
import { useMedia } from 'react-media-match';
import { getUserLocale } from '@empiriecom/module-components/IntlProvider';
import useCookieConsent from '@empiriecom/module-components/hooks/useCookieConsent';
import useConfig from '@empiriecom/module-components/hooks/useConfig';
import { FragmentConfiguration } from '@/config/default';
import Card from '../../Card';

const LoadBeeWrapper = styled.div`
  min-height: 30rem;
`;

export const LoadBee: FunctionComponent<{ ean: string }> = ({ ean = '' }): ReactElement => {
  const {
    externalServices: {
      loadbee: { apiKey, asyncScript, consentGroup, containerName, scriptUrl },
    },
  }: FragmentConfiguration = useConfig();

  const isConsentAllowed = useCookieConsent(consentGroup);

  const isMobile = useMedia({ desktop: false, mobile: true, tablet: false });
  const intl = useIntl();
  const messages = defineMessages({
    cardHeadline: {
      defaultMessage: 'Herstellerinformationen',
      id: 'loadbee.card.headline',
    },
    collapseCard: {
      defaultMessage: 'Weniger Hersteller-Informationen anzeigen',
      id: 'loadbee.card.collapse',
    },
    expandCard: {
      defaultMessage: 'Alle Hersteller-Informationen anzeigen',
      id: 'loadbee.card.expand',
    },
  });

  const userLocale = getUserLocale().replace('-', '_');

  useEffect(() => {
    if (isConsentAllowed) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const scriptToRemove = document.getElementById('loadbee-script');
      scriptToRemove?.parentNode?.removeChild(scriptToRemove);
      const script = document.createElement('script');
      script.setAttribute('src', scriptUrl);
      script.setAttribute('id', 'loadbee-script');
      if (asyncScript) {
        script.setAttribute('async', 'async');
      }
      document.head.insertBefore(script, document.head.firstElementChild);
    }
  }, [isConsentAllowed, ean]);

  return (
    <>
      {isConsentAllowed && (
        <div className="loadbeeToggle">
          <Card
            collapsible
            collapseLabelText={intl.formatMessage(messages.collapseCard)}
            contentPadding
            expandLabelText={intl.formatMessage(messages.expandCard)}
            headline={intl.formatMessage(messages.cardHeadline)}
            scrollTarget={isMobile ? '#mobile-card-scroll-target' : '#thumbnail-slider'}
          >
            <LoadBeeWrapper data-testid="loadbee-card">
              <div
                className={containerName}
                data-loadbee-apikey={apiKey}
                data-loadbee-gtin={ean}
                data-loadbee-locale={userLocale}
              />
            </LoadBeeWrapper>
          </Card>
        </div>
      )}
    </>
  );
};

export default LoadBee;
